import { useState, useEffect, useCallback } from 'react';
import { Alert } from 'react-native';
import { Customer, PaymentMethod } from '../../shared/types';
import { ERROR_MESSAGES } from '../../shared/constants';
import { validateStripeId, validateEmail } from '../../shared/utils/validation';

interface UseCustomerOptions {
  customerId?: string;
  autoFetch?: boolean;
  onError?: (error: string) => void;
  showErrorAlert?: boolean;
  showSuccessAlert?: boolean;
}

interface UseCustomerReturn {
  customer: Customer | null;
  paymentMethods: PaymentMethod[];
  loading: boolean;
  error: string | null;
  // Backward compatibility getters
  isLoading: boolean; // Maps to loading
  isError: boolean; // Maps to !!error
  refetch: () => Promise<void>;
  createCustomer: (params: {
    email: string;
    name?: string;
    phone?: string;
    metadata?: Record<string, string>;
  }) => Promise<Customer | null>;
  updateCustomer: (params: {
    email?: string;
    name?: string;
    phone?: string;
    metadata?: Record<string, string>;
  }) => Promise<boolean>;
  deleteCustomer: () => Promise<boolean>;
  fetchPaymentMethods: () => Promise<PaymentMethod[]>;
  addPaymentMethod: (paymentMethodId: string) => Promise<boolean>;
  removePaymentMethod: (paymentMethodId: string) => Promise<boolean>;
  setDefaultPaymentMethod: (paymentMethodId: string) => Promise<boolean>;
}

export const useCustomer = (
  options: UseCustomerOptions = {}
): UseCustomerReturn => {
  const {
    customerId,
    autoFetch = true,
    onError,
    showErrorAlert = true,
    showSuccessAlert = true,
  } = options;

  const [customer, setCustomer] = useState<Customer | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleError = useCallback(
    (errorMessage: string) => {
      setError(errorMessage);
      onError?.(errorMessage);

      if (showErrorAlert) {
        Alert.alert('Customer Error', errorMessage);
      }

      console.error('Customer error:', errorMessage);
    },
    [onError, showErrorAlert]
  );

  const handleSuccess = useCallback(
    (message: string) => {
      if (showSuccessAlert) {
        Alert.alert('Success', message);
      }
    },
    [showSuccessAlert]
  );

  const fetchCustomer = useCallback(
    async (id: string) => {
      if (!validateStripeId(id, 'customer')) {
        handleError('Invalid customer ID');
        return null;
      }

      try {
        const response = await fetch(`/api/payments/customer/${id}`);

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || ERROR_MESSAGES.CUSTOMER_NOT_FOUND);
        }

        const data = await response.json();
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : ERROR_MESSAGES.CUSTOMER_NOT_FOUND;
        handleError(errorMessage);
        return null;
      }
    },
    [handleError]
  );

  const refetch = useCallback(async () => {
    if (!customerId) {
      handleError('Customer ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const customerData = await fetchCustomer(customerId);
      setCustomer(customerData);
    } finally {
      setLoading(false);
    }
  }, [customerId, fetchCustomer, handleError]);

  const createCustomer = useCallback(
    async (params: {
      email: string;
      name?: string;
      phone?: string;
      metadata?: Record<string, string>;
    }): Promise<Customer | null> => {
      if (!validateEmail(params.email)) {
        handleError('Invalid email address');
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch('/api/payments/customer', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || 'Failed to create customer');
        }

        const customerData = await response.json();
        setCustomer(customerData);
        handleSuccess('Customer created successfully!');
        return customerData;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to create customer';
        handleError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [handleError, handleSuccess]
  );

  const updateCustomer = useCallback(
    async (params: {
      email?: string;
      name?: string;
      phone?: string;
      metadata?: Record<string, string>;
    }): Promise<boolean> => {
      if (!customerId) {
        handleError('Customer ID is required');
        return false;
      }

      if (params.email && !validateEmail(params.email)) {
        handleError('Invalid email address');
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/payments/customer/${customerId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(params),
        });

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || 'Failed to update customer');
        }

        await refetch(); // Refresh customer data
        handleSuccess('Customer updated successfully!');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to update customer';
        handleError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [customerId, refetch, handleError, handleSuccess]
  );

  const deleteCustomer = useCallback(async (): Promise<boolean> => {
    if (!customerId) {
      handleError('Customer ID is required');
      return false;
    }

    return new Promise(resolve => {
      Alert.alert(
        'Delete Customer',
        'Are you sure you want to delete this customer? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
            onPress: () => resolve(false),
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              setLoading(true);
              setError(null);

              try {
                const response = await fetch(
                  `/api/payments/customer/${customerId}`,
                  {
                    method: 'DELETE',
                  }
                );

                if (!response.ok) {
                  const errorData = await response
                    .json()
                    .catch(() => ({ error: 'Network error' }));
                  throw new Error(
                    errorData.error || 'Failed to delete customer'
                  );
                }

                setCustomer(null);
                setPaymentMethods([]);
                handleSuccess('Customer deleted successfully!');
                resolve(true);
              } catch (err) {
                const errorMessage =
                  err instanceof Error
                    ? err.message
                    : 'Failed to delete customer';
                handleError(errorMessage);
                resolve(false);
              } finally {
                setLoading(false);
              }
            },
          },
        ]
      );
    });
  }, [customerId, handleError, handleSuccess]);

  const fetchPaymentMethods = useCallback(async (): Promise<
    PaymentMethod[]
  > => {
    if (!customerId) {
      handleError('Customer ID is required');
      return [];
    }

    try {
      const response = await fetch(
        `/api/payments/customer/${customerId}/payment-methods`
      );

      if (!response.ok) {
        const errorData = await response
          .json()
          .catch(() => ({ error: 'Network error' }));
        throw new Error(errorData.error || 'Failed to fetch payment methods');
      }

      const data = await response.json();
      const methods = data.paymentMethods || [];
      setPaymentMethods(methods);
      return methods;
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to fetch payment methods';
      handleError(errorMessage);
      return [];
    }
  }, [customerId, handleError]);

  const addPaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<boolean> => {
      if (!customerId) {
        handleError('Customer ID is required');
        return false;
      }

      try {
        const response = await fetch(
          `/api/payments/customer/${customerId}/payment-methods`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentMethodId }),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(errorData.error || 'Failed to add payment method');
        }

        await fetchPaymentMethods(); // Refresh payment methods
        handleSuccess('Payment method added successfully!');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to add payment method';
        handleError(errorMessage);
        return false;
      }
    },
    [customerId, fetchPaymentMethods, handleError, handleSuccess]
  );

  const removePaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<boolean> => {
      if (!customerId) {
        handleError('Customer ID is required');
        return false;
      }

      return new Promise(resolve => {
        Alert.alert(
          'Remove Payment Method',
          'Are you sure you want to remove this payment method?',
          [
            {
              text: 'Cancel',
              style: 'cancel',
              onPress: () => resolve(false),
            },
            {
              text: 'Remove',
              style: 'destructive',
              onPress: async () => {
                try {
                  const response = await fetch(
                    `/api/payments/customer/${customerId}/payment-methods/${paymentMethodId}`,
                    {
                      method: 'DELETE',
                    }
                  );

                  if (!response.ok) {
                    const errorData = await response
                      .json()
                      .catch(() => ({ error: 'Network error' }));
                    throw new Error(
                      errorData.error || 'Failed to remove payment method'
                    );
                  }

                  await fetchPaymentMethods(); // Refresh payment methods
                  handleSuccess('Payment method removed successfully!');
                  resolve(true);
                } catch (err) {
                  const errorMessage =
                    err instanceof Error
                      ? err.message
                      : 'Failed to remove payment method';
                  handleError(errorMessage);
                  resolve(false);
                }
              },
            },
          ]
        );
      });
    },
    [customerId, fetchPaymentMethods, handleError, handleSuccess]
  );

  const setDefaultPaymentMethod = useCallback(
    async (paymentMethodId: string): Promise<boolean> => {
      if (!customerId) {
        handleError('Customer ID is required');
        return false;
      }

      try {
        const response = await fetch(
          `/api/payments/customer/${customerId}/default-payment-method`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ paymentMethodId }),
          }
        );

        if (!response.ok) {
          const errorData = await response
            .json()
            .catch(() => ({ error: 'Network error' }));
          throw new Error(
            errorData.error || 'Failed to set default payment method'
          );
        }

        await Promise.all([refetch(), fetchPaymentMethods()]); // Refresh both customer and payment methods
        handleSuccess('Default payment method updated successfully!');
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to set default payment method';
        handleError(errorMessage);
        return false;
      }
    },
    [customerId, refetch, fetchPaymentMethods, handleError, handleSuccess]
  );

  useEffect(() => {
    if (autoFetch && customerId) {
      refetch();
      fetchPaymentMethods();
    }
  }, [autoFetch, customerId, refetch, fetchPaymentMethods]);

  return {
    customer,
    paymentMethods,
    loading,
    error,
    // Backward compatibility getters
    isLoading: loading,
    isError: !!error,
    refetch,
    createCustomer,
    updateCustomer,
    deleteCustomer,
    fetchPaymentMethods,
    addPaymentMethod,
    removePaymentMethod,
    setDefaultPaymentMethod,
  };
};
